"use client";

import { useEffect, useRef, useCallback } from "react";
import styles from "./filter-overlay.module.css";

interface FilterOverlayPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onApply?: () => void;
  onReset?: () => void;
}

export default function FilterOverlayPanel({
  open,
  onClose,
  title,
  children,
  anchorRef,
  onApply,
  onReset,
}: FilterOverlayPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target)) {
        // Don't close if clicking on anchor trigger
        if (anchorRef?.current && anchorRef.current.contains(target)) return;
        onClose();
      }
    };
    // Use timeout to avoid the click that opened the panel
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [open, onClose, anchorRef]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (!open) return;
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Mobile backdrop */}
      <div className={styles.backdrop} onClick={onClose} />

      <div ref={panelRef} className={styles.panel}>
        <div className={styles.header}>
          <h4 className={styles.title}>{title}</h4>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={styles.body}>{children}</div>

        <div className={styles.footer}>
          {onReset && (
            <button
              type="button"
              className={styles.resetBtn}
              onClick={onReset}
            >
              Reset
            </button>
          )}
          {onApply && (
            <button
              type="button"
              className={styles.applyBtn}
              onClick={onApply}
            >
              Apply
            </button>
          )}
        </div>
      </div>
    </>
  );
}
